import React from "react";
import { Banner, Button, ScreenSizeRender, useScreenSize } from "@jsq/ds/core";
import { Formik, Form } from "formik";
import classnames from "classnames";
import { type UnauthenticatedArenaObjectType } from "apps/portal/Portal/common/types";
import { FormEmailInput } from "@jsq/ds/forms";
import styles from "../../common/styles.scss";
import { schema } from "./schema";
import { LoginCard } from "../../common/LoginCard";
import { UserContext } from "../../common/constants";
import { defaultSingleManagerPoweredByMessage } from "../../common/LoginCard/constants";

type ResetPasswordFormik = {
  email: string;
};
type Props = {
  handleResetPasswordRequestSubmit: (values: ResetPasswordFormik) => void;
  email: string;
  message?: string;
  error?: string;
  arena?: UnauthenticatedArenaObjectType;
  showPoweredByHeader?: boolean;
  onClickBack?: () => void;
  userContext?: UserContext;
};

const ForgotPasswordForm: React.FC<Props> = ({
  handleResetPasswordRequestSubmit,
  email,
  message,
  error,
  arena,
  showPoweredByHeader = true,
  onClickBack,
  userContext,
}) => {
  const screenSize = useScreenSize();

  const isGpLogin = userContext === UserContext.Arena;
  const buttonText = !isGpLogin && arena?.signInButtonTextColor;
  const buttonBackground = !isGpLogin && arena?.signInButtonBackgroundColor;
  const buttonVariantOn = buttonBackground || buttonText;

  return (
    <LoginCard
      className={styles.Login__resetPasswordCard}
      showCardTitle
      showPoweredByHeader={showPoweredByHeader}
      poweredByHelpMessage={defaultSingleManagerPoweredByMessage(arena?.name)}
      cardTitle="Reset password"
      titleDataTestId="forget-password-title"
      description={
        message || (
          <>
            Enter your email address and we&apos;ll send
            <br /> instructions for resetting your password
          </>
        )
      }
      descriptionDataTestId="email-password-information-text"
      onGoBackClick={screenSize === "small" ? onClickBack : undefined}
      goBackButtonText={screenSize === "small" ? "Go back" : undefined}
    >
      {onClickBack && (
        <ScreenSizeRender medium large>
          <Button
            icon="chevron-left"
            variant="muted"
            className={styles.Login__backButton}
            onClick={onClickBack}
          >
            Back
          </Button>
        </ScreenSizeRender>
      )}
      {error && <Banner variant="danger" title="Error" description={error} />}

      <Formik
        initialValues={{ email }}
        validationSchema={schema}
        onSubmit={handleResetPasswordRequestSubmit}
      >
        <Form className={styles.Login__form}>
          <FormEmailInput
            label="Email address"
            name="email"
            className={styles.Login__input}
            data-cy="email-address-input"
            data-testid="email-address"
            isFullWidth
          />
          <Button
            type="submit"
            isFullWidth
            className={classnames("inline-sm", styles.Login__submitButton, {
              "stacked-md": screenSize === "small",
            })}
            data-cy="send-reset-information-button"
            variant={buttonVariantOn ? "custom" : "primary"}
            backgroundColor={buttonBackground || undefined}
            textColor={buttonText || undefined}
          >
            Send reset information
          </Button>
        </Form>
      </Formik>
    </LoginCard>
  );
};

export default ForgotPasswordForm;
